import { Box, Stack, TextField } from "@mui/material";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";

import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
} from "@4uhub/lib4uhub";
import { FilterParamEntry, IMacroFilterProps } from "./models";

const translationPath = "page.4uBus.logs.syncTransaction.";

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
  statusItems,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);

  const { t } = useTranslation();

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const [resetDateRange, setResetDateRange] = useState<boolean>(false);
  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  const [enableFields, setEnableFields] = useState<boolean>(true);

  const handleStartDate = (date: Date | null) => {
    setFilter({ createdStart: { ...filterParams.createdStart, value: date } });
  };

  const handleEndDate = (date: Date | null) => {
    if (filterParams.createdEnd)
      setFilter({ createdEnd: { ...filterParams.createdEnd, value: date } });
  };

  const [selectedStatus, setSelectedStatus] = useState<ISelectType | null>(
    null,
  );

  const handleSelectStatus = useCallback(
    (value: ISelectType | null) => {
      setFilter({
        statusCode: { ...filterParams.statusCode, value: value?.id ?? "" },
      });
      setSelectedStatus(value);
    },
    [setFilter, filterParams],
  );

  const statuses = useMemo(() => {
    const statusItemsArray: ISelectType[] = Object.entries(statusItems).map(
      ([key, value]) => ({
        id: key,
        name: t(translationPath + `status.${value.key}`),
        code: key.charAt(0),
      }),
    );

    return statusItemsArray;
  }, [t, statusItems]);

  return (
    <Stack flexDirection="row" gap={2} alignItems="center">
      <DateRangeSelect
        setEndDate={handleEndDate}
        setStartDate={handleStartDate}
        setEnableFields={setEnableFields}
        resetToToday={resetDateRange}
      />

      {(Object.entries(filter) as FilterParamEntry[]).map(([key, v]) => {
        const isDate = key === "createdStart" || key === "createdEnd";
        const isStatus = key === "statusCode";

        return (
          <React.Fragment key={key}>
            {isDate && (
              <>
                {typeof v?.value !== "string" && (
                  <Box sx={{ flex: "0 0 160px" }}>
                    <DatePickerUncontrolled
                      value={v?.value}
                      onChange={(e) => setFilter({ [key]: { ...v, value: e } })}
                      label={t(translationPath + v?.label) || "Date"}
                      isClearable={false}
                      disable={enableFields}
                    />
                  </Box>
                )}
              </>
            )}

            {isStatus && (
              <AutoCompleteUncontrolled
                label={t(translationPath + v?.label)}
                onValueSelected={handleSelectStatus}
                value={selectedStatus}
                options={statuses}
                size="small"
                fullWidth
              />
            )}

            {!isDate && !isStatus && (
              <TextField
                value={v?.value}
                size="small"
                label={t(translationPath + v?.label)}
                fullWidth
                onChange={(e) => {
                  setFilter({ [key]: { ...v, value: e.target.value } });
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      <FiltersActions
        onFilter={filterHandler}
        onClean={cleanHandler}
        showCleanBtn={showCleanBtn}
      />
    </Stack>
  );
};

export default memo(MacroFilter);
